import { useEffect, useState } from "react";
import Dashboard from "./ui/DashboardComponents";
import SettingsPage from "./TabPages/SettingsPage";
import ReviewQueuePage from "./TabPages/ReviewQueuePage";
import BulkActionsPage from "./TabPages/BulkActionsPage";
import Form12bb from "../components/ui/Form12BB/Index";
import { IoSettings } from "react-icons/io5";
import { CalendarAlt, RefreshCw } from "../../../../../components/icons/Icons";
import { useSelector } from "react-redux";

const TDSModuleLayout = () => {
  const themeStore = useSelector((state) => state.theme);
  const theme  = themeStore?.theme || localStorage.getItem("theme") || "light";
  const isDark = theme === "dark";

  const pages = [
    { id: 1, label: "Dashboard",     component: <Dashboard />,       hash: "dashboard" },
    { id: 2, label: "TDS Form 12BB", component: <Form12bb />,        hash: "form12bb"  },
    { id: 3, label: "Review Queue",  component: <ReviewQueuePage />, hash: "review"    },
    { id: 4, label: "Bulk Actions",  component: <BulkActionsPage />, hash: "bulk"      },
    { id: 5, label: "Settings",      component: <SettingsPage />,    hash: "settings"  },
  ];

  const [activeMenu,  setActiveMenu]  = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  /* theme tokens */
  const bg        = isDark ? "#0f0f1a"               : "#ffffff";
  const text      = isDark ? "#e5e7eb"               : "#1f2937";
  const border    = isDark ? "#2a2a3a"               : "#e5e7eb";
  const accent    = isDark ? "#9b51e0"               : "#7c3aed";
  const muted     = isDark ? "rgba(255,255,255,0.45)": "#6b7280";
  const badgeBg   = isDark ? "rgba(255,255,255,0.05)": "#f3f4f6";
  const refreshBg = "linear-gradient(90deg,#7c3aed,#5b21b6)";

  const fmt = (date) => {
    const m = Math.floor((Date.now() - date) / 60000);
    if (m < 1)  return "Just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h}h ago` : "Long ago";
  };

  const getTabId = () => {
    const hash    = window.location.hash.slice(1);
    const tabHash = hash.split("#")[1] || hash.split("#")[0] || "dashboard";
    return pages.find((p) => p.hash === tabHash)?.id ?? 1;
  };

  useEffect(() => {
    setActiveMenu(getTabId());
    const saved = localStorage.getItem("tdsLastUpdated");
    const now   = new Date();
    setLastUpdated(saved ? new Date(saved) : now);
    localStorage.setItem("tdsLastUpdated", now.toISOString());
  }, []);

  useEffect(() => {
    const fn = () => setActiveMenu(getTabId());
    window.addEventListener("hashchange", fn);
    return () => window.removeEventListener("hashchange", fn);
  }, []);

  const handleTabClick = (item) => {
    const base = window.location.hash.slice(1).split("#")[0] || "tds";
    window.location.hash = `${base}#${item.hash}`;
  };

  const handleRefresh = () => {
    const now = new Date();
    setLastUpdated(now);
    localStorage.setItem("tdsLastUpdated", now.toISOString());
    window.dispatchEvent(new CustomEvent("tdsModuleRefresh", { detail: { timestamp: now } }));
  };

  const activePage = pages.find((p) => p.id === activeMenu)?.component;

  return (
    /* Outer wrapper — fills page-body exactly, no overflow */
    <div style={{
      display       : "flex",
      flexDirection : "column",
      width         : "100%",
      minWidth      : 0,
      height        : "100%",
      boxSizing     : "border-box",
      overflow      : "hidden",
      minHeight     : 0,
    }}>

      {/* ── Top bar ── */}
      <div style={{
        display        : "flex",
        alignItems     : "center",
        justifyContent : "space-between",
        width          : "100%",
        minWidth       : 0,
        boxSizing      : "border-box",
        padding        : "0 12px",
        height         : "40px",
        flexShrink     : 0,
        backgroundColor: bg,
        borderBottom   : `1px solid ${border}`,
        gap            : "8px",
      }}>

        {/* Left: title + tabs — allow shrinking */}
        <div style={{
          display    : "flex",
          alignItems : "center",
          minWidth   : 0,
          flex       : 1,
          overflow   : "hidden",
          gap        : "2px",
        }}>
          {/* Title */}
          <div style={{
            display      : "flex",
            alignItems   : "center",
            gap          : "5px",
            flexShrink   : 0,
            paddingRight : "10px",
            marginRight  : "6px",
            borderRight  : `1px solid ${border}`,
          }}>
            <IoSettings style={{ fontSize: "13px", color: accent }} />
            <span style={{ fontSize: "12px", fontWeight: 700, color: text, whiteSpace: "nowrap" }}>
              TDS Module
            </span>
          </div>

          {/* Tabs */}
          {pages.map((item) => {
            const active = activeMenu === item.id;
            return (
              <div key={item.id} style={{ position: "relative", flexShrink: 0 }}>
                <button onClick={() => handleTabClick(item)} style={{
                  background : "none",
                  border     : "none",
                  color      : active ? accent : muted,
                  fontSize   : "11px",
                  fontWeight : active ? 600 : 400,
                  padding    : "4px 8px",
                  cursor     : "pointer",
                  whiteSpace : "nowrap",
                  lineHeight : "1",
                }}>
                  {item.label}
                </button>
                {active && (
                  <div style={{
                    position        : "absolute",
                    bottom          : 0,
                    left            : "8px",
                    right           : "8px",
                    height          : "2px",
                    backgroundColor : accent,
                    borderRadius    : "2px 2px 0 0",
                  }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Right: last updated + refresh — never shrink below content */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          <div style={{
            display         : "flex",
            alignItems      : "center",
            gap             : "4px",
            padding         : "3px 8px",
            backgroundColor : badgeBg,
            border          : `1px solid ${border}`,
            borderRadius    : "5px",
            fontSize        : "10px",
            whiteSpace      : "nowrap",
            color           : muted,
          }}>
            <CalendarAlt style={{ fontSize: "10px" }} />
            <span>Updated: <strong style={{ color: text }}>{fmt(lastUpdated)}</strong></span>
          </div>

          <button onClick={handleRefresh} style={{
            display      : "flex",
            alignItems   : "center",
            gap          : "4px",
            padding      : "4px 10px",
            background   : refreshBg,
            color        : "#fff",
            fontSize     : "10px",
            fontWeight   : 500,
            border       : "none",
            borderRadius : "5px",
            cursor       : "pointer",
            whiteSpace   : "nowrap",
          }}>
            <RefreshCw style={{ fontSize: "10px" }} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Page content ── */}
      <div style={{
        flex            : 1,
        minHeight       : 0,
        overflowY       : "auto",
        overflowX       : "hidden",
        width           : "100%",
        minWidth        : 0,
        boxSizing       : "border-box",
        backgroundColor : isDark ? "#0a0a14" : "#f3f4f6",
        color           : text,
      }}>
        {activePage}
      </div>

    </div>
  );
};

export default TDSModuleLayout;
